import * as React from "react";
import { useState, useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { Home, Zap, Shield, Lightbulb, Thermometer, Activity } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ConnectionBanner,
  StatusBar,
  PillTabs,
  GlanceCard,
  ZoneSection,
  SensorBadge,
  Tile,
  KioskToggle,
} from "@/components/system";
import { Button } from "@/components/ui/button";
import { useHomey } from "@/hooks/useHomey";
import { useFullscreen } from "@/hooks/useFullscreen";
import { buildGlance, buildZoneSections } from "@/lib/homey-viewmodel";
import { normalizeKioskSettings } from "@/lib/kiosk";
import { normalizeHiddenSettings } from "@/lib/hidden-devices";
import { trpc } from "@/lib/trpc";
import { DeviceTileFor } from "./home/DeviceTileFor";
import { useDeviceCommands } from "./home/useDeviceCommands";
import { EnergyView } from "./home/EnergyView";
import { FlowsView } from "./home/FlowsView";
import { SecurityView } from "./home/SecurityView";
import { KioskController } from "./home/KioskController";

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

const TABS = [
  { id: "home", label: "Home" },
  { id: "energy", label: "Energy" },
  { id: "flows", label: "Flows" },
  { id: "security", label: "Security" },
] as const;

// ---------------------------------------------------------------------------
// Skeleton loading state
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      {/* Glance row skeleton */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-2xl" />
        ))}
      </div>
      {/* Zone grid skeletons */}
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="space-y-3">
          <Skeleton className="h-5 w-32" />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, j) => (
              <Skeleton key={j} className="h-24 rounded-2xl" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty / no connection state
// ---------------------------------------------------------------------------

function NoConnectionState() {
  return (
    <div className="flex flex-col items-center justify-center gap-6 py-24 text-center">
      <Tile domain="neutral" className="w-fit p-6">
        <Home className="size-10 text-muted-foreground" aria-hidden />
      </Tile>
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Connect your Homey</h2>
        <p className="max-w-sm text-sm text-muted-foreground">
          Link your Homey hub to start controlling your home from this dashboard.
        </p>
      </div>
      <Button asChild>
        <Link to="/dashboard/settings">Go to Settings</Link>
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Home tab — live view
// ---------------------------------------------------------------------------

function HomeView({ hiddenIds }: { hiddenIds: Set<string> }) {
  const { snapshot } = useHomey();
  const commands = useDeviceCommands();

  const glance = useMemo(
    () => (snapshot ? buildGlance(snapshot, hiddenIds) : null),
    [snapshot, hiddenIds],
  );
  const zoneSections = useMemo(
    () => (snapshot ? buildZoneSections(snapshot, hiddenIds) : []),
    [snapshot, hiddenIds],
  );
  // Device-by-id lookup so media tiles can derive their MediaVM from the raw snap.
  const devicesById = useMemo(
    () => new Map((snapshot?.devices ?? []).map((d) => [d.id, d])),
    [snapshot],
  );

  if (!glance) return null;

  // Track current floor heading to insert section breaks
  let lastFloorName: string | null | undefined = undefined;

  return (
    <div className="space-y-8">
      {/* Glance row */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {glance.powerNowW !== null && (
          <GlanceCard
            label="Power now"
            value={glance.powerNowW}
            unit="W"
            precision={0}
            icon={<Zap />}
            domain="energy"
          />
        )}
        <GlanceCard label="Lights on" value={glance.lightsOn} icon={<Lightbulb />} domain="light" />
        {glance.insideTemp !== null && (
          <GlanceCard
            label="Inside"
            value={glance.insideTemp}
            unit="°"
            precision={1}
            icon={<Thermometer />}
            domain="climate"
          />
        )}
        <GlanceCard
          label="Alerts"
          value={glance.securityAlerts}
          icon={<Shield />}
          domain="security"
        />
      </div>

      {/* Zone sections, grouped with floor headings */}
      <div className="space-y-8">
        {zoneSections.map((section) => {
          const showFloorHeading =
            section.floorName !== null && section.floorName !== lastFloorName;
          lastFloorName = section.floorName;

          return (
            <React.Fragment key={section.id}>
              {showFloorHeading && (
                <p className="text-xs font-medium tracking-widest text-muted-foreground uppercase">
                  {section.floorName}
                </p>
              )}
              <ZoneSection
                name={section.name}
                summary={
                  section.temperature !== null ? (
                    <SensorBadge
                      icon={<Activity />}
                      value={section.temperature}
                      unit="°"
                      precision={1}
                      domain="climate"
                      aria-label={`${section.name} temperature: ${section.temperature.toFixed(1)}°`}
                    />
                  ) : undefined
                }
              >
                {section.devices.map((vm) => (
                  <DeviceTileFor
                    key={vm.id}
                    vm={vm}
                    device={devicesById.get(vm.id)}
                    commands={commands}
                  />
                ))}
              </ZoneSection>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * DashboardContent — Home screen for the Homey wall dashboard.
 *
 * States:
 * - No connection (hasConnection === false): CTA to /dashboard/settings.
 * - Loading: skeleton glance row + zone grids.
 * - Live: ConnectionBanner + StatusBar + PillTabs + the active sub-view.
 *   Home (glance row + floor-grouped ZoneSections), Energy (gauge + stats +
 *   top consumers + 24h chart), Flows (grouped FlowButtons + favorites), and
 *   Security (alarm sensor tiles) are all live (Phase 5).
 */
export function DashboardContent() {
  const { snapshot, isLoading, socketStatus, homeyStatus, hasConnection } = useHomey();
  const [activeTab, setActiveTab] = useState<string>("home");

  // Fullscreen state is owned here (the layout owner) so the header toggle and
  // the KioskController's wake lock share the same source of truth.
  const {
    isFullscreen,
    toggle: toggleFullscreen,
    supported: fullscreenSupported,
  } = useFullscreen();

  // Kiosk preferences (jsonb may be null/partial → normalize defensively).
  const { data: settingsRow } = trpc.homey.getDashboardSettings.useQuery(undefined, {
    enabled: hasConnection,
  });
  const kioskSettings = useMemo(() => normalizeKioskSettings(settingsRow?.kiosk), [settingsRow]);

  // Devices the user has hidden — excluded from the Home view and glance/status aggregates.
  const hiddenIds = useMemo(
    () => new Set(normalizeHiddenSettings(settingsRow?.hidden).deviceIds),
    [settingsRow],
  );

  const glance = useMemo(
    () => (snapshot ? buildGlance(snapshot, hiddenIds) : null),
    [snapshot, hiddenIds],
  );

  if (!hasConnection) {
    return <NoConnectionState />;
  }

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="space-y-4 p-4 md:p-6">
      {/* Connection degradation banner (fixed, shown only when degraded) */}
      <ConnectionBanner socketStatus={socketStatus} homeyStatus={homeyStatus} />

      {/* Kiosk runtime: idle screensaver, presence/camera wake, wake lock. */}
      <KioskController settings={kioskSettings} snapshot={snapshot} isFullscreen={isFullscreen} />

      {/* Status bar with a fullscreen kiosk toggle on the right. */}
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <StatusBar
            outsideTemp={glance?.outsideTemp}
            powerNowW={glance?.powerNowW}
            securityAlerts={glance?.securityAlerts}
            presence={snapshot?.presence}
          />
        </div>
        {fullscreenSupported && (
          <KioskToggle isFullscreen={isFullscreen} onToggle={() => void toggleFullscreen()} />
        )}
      </div>

      {/* Pill tab navigation */}
      <PillTabs tabs={TABS.map((t) => ({ ...t }))} value={activeTab} onChange={setActiveTab} />

      {/* Tab content. snapshot is guaranteed loaded in this branch (isLoading
          short-circuits above), but guard for the type and a defensive null. */}
      {activeTab === "home" && <HomeView hiddenIds={hiddenIds} />}
      {snapshot && activeTab === "energy" && <EnergyView snapshot={snapshot} />}
      {snapshot && activeTab === "flows" && <FlowsView snapshot={snapshot} />}
      {snapshot && activeTab === "security" && <SecurityView snapshot={snapshot} />}
    </div>
  );
}

export default DashboardContent;
